import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  Col,
  Row,
  TextDisplay,
  Link,
  CaptionText,
  IconButton,
  CloseIcon,
} from '@paypalcorp/pp-react'
import AppCard from './sections/app-card'
import UpgradeHubAlertError from '../alerts/upgrade-hub-alert-error'
import { injectWorldReady } from '@paypalcorp/worldready-react'
import { useAnalytics, useTrackShown } from '../analytics/analytics-context'
import { useApplicationContext } from '../../../context/ApplicationContext'
import useAPI from '../../../hooks/api'
import logUtil from '../../../utils/log-util'

const OutdatedAppsBanner = ({
  outdatedApps = [],
  isOutdatedAppsLoading,
  outdatedAppsError,
  closeOutdatedAppsBanner,
}) => {
  const { upgradehub_v2 } = useApplicationContext()
  const { trackPress } = useAnalytics()
  const treatmentName = upgradehub_v2
  const experimentName = treatmentName.split('_').slice(1).join('_')
  const [bannerContent, setBannerContent] = useState(null)
  const navigate = useNavigate()

  const [
    { data: ecmData, error: ecmError, isLoading: ecmLoading },
  ] = useAPI.get('/dashboard/api/content/outdated-apps-banner-section')

  useTrackShown({
    treatmentName,
    experimentName,
    component: 'out_of_date_apps',
    count: outdatedApps?.length ?? 0,
  })

  useEffect(() => {
    if (!ecmLoading) {
      if (ecmData) {
        setBannerContent(ecmData.content?.appSection?.content || null)
      } else if (ecmError) {
        logUtil.error(ecmError)
        logUtil.info('Error fetching ECM content.')
      }
    }
  }, [ecmLoading])

  if (ecmLoading) {
    return null
  }

  const handleClose = () => {
    trackPress({ component: 'upgrade_banner_button' })
    closeOutdatedAppsBanner()
  }

  if (ecmLoading || isOutdatedAppsLoading) return null

  if (outdatedAppsError || ecmError || !ecmData) {
    return <UpgradeHubAlertError />
  }

  if (!Array.isArray(outdatedApps) || outdatedApps.length === 0) return null

  const handleViewAllIntegrations = () => {
    trackPress({
      component: 'link',
      linkName: ' View all integrations',
      redirectUrl: '/upgrade-your-apps',
    })
    navigate('/upgrade-your-apps')
  }

  const titleMessage =
    outdatedApps && outdatedApps?.length === 1
      ? bannerContent?.singleAppHeader
      : bannerContent?.multipleAppsHeader.replace(
          '{count}',
          outdatedApps?.length,
        )
  const subHeader = bannerContent?.subHeader

  return (
    <Card>
      <Row className="banner-header">
        <Col xs={10}>
          <TextDisplay
            className="outdated-app-banner-title no-padding"
            name="outdate-app-banner-title"
            label=""
            value={titleMessage}
          />
          <CaptionText className="caption-subtitle no-padding">
            {subHeader}
          </CaptionText>
        </Col>
        <Col cs={2} align="right">
          <IconButton
            size="sm"
            onClick={handleClose}
            buttonType="tertiary"
            svgIconComponent={CloseIcon}
            aria-label="close"
            id="closeBannerBtn"
          />
        </Col>
      </Row>
      <Row>
        {Array.isArray(outdatedApps) &&
          outdatedApps?.slice(0, 3).map((app, index) => (
            <Col
              key={index}
              className={outdatedApps?.length === 1 ? 'single-app' : 'all-apps'}
            >
              <AppCard app={app} />
            </Col>
          ))}
      </Row>
      {outdatedApps?.length > 3 && (
        <Row>
          <Col xs={12} className="cards-col">
            <Link
              className="link-all-integrations"
              onClick={handleViewAllIntegrations}
            >
              View all integrations
            </Link>
          </Col>
        </Row>
      )}
    </Card>
  )
}

export default injectWorldReady(OutdatedAppsBanner)
